import {
    SET_CURRENT_PROMPT,
    SET_NEW_USER,
    UPDATE_ROLE,
    USER_LOGIN,
    USER_LOGOUT,
    START_IMPERSONATION,
    STOP_IMPERSONATION
} from "../actions/auth.action";

import {
    LOCAL_STORAGE_JWT_TOKEN,
    LOCAL_STORAGE_LOGGED_IN_USER_DATA,
    USER_LOGIN_METHOD
} from "../../constants/Misc";

import { googleLogout } from "@react-oauth/google";
import Communications from "../../services/communications.service";
import { CommonService } from "../../services";
import { Misc } from "../../constants";
import commonService from "@/services/common.service.ts";
import { IUser } from "@/models/user.model"; // Assuming IUser is imported if not already

export interface IAuthReducerState {
    user: IUser | any | null; // any is kept for backward compatibility as per original code
    token: string | null;
    loginMethod: string | null;
    isNewUser: boolean | null;
    currentPrompt: string | null;
    isAdmin: boolean; // Added
    originalAdminState: { // Store minimal necessary info
        user: IUser | any | null;
        token: string | null;
        isAdmin: boolean;
    } | null;
}

const storedUser = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA);
const initialUser: IUser | null = storedUser ? JSON.parse(storedUser) : null;

const initialState: IAuthReducerState = {
    user: initialUser,
    token: CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN),
    loginMethod: CommonService._localStorage.getItem(USER_LOGIN_METHOD) || "username",
    isNewUser: false,
    currentPrompt: '',
    isAdmin: !!(initialUser?.is_admin || initialUser?.role === 'admin'), // Added
    originalAdminState: null, // Added
};

const authReducer = (state = initialState, action: any): IAuthReducerState => {

    switch (action.type) {
        case USER_LOGIN: {
            localStorage.removeItem('originalAdminState'); // Clear any impersonation state
            const resp = action.payload.user as IUser; // Type assertion
            commonService.handleMixpanelIdentify(resp);
            commonService.handleMixpanelTrack("Login successful", {
                name: resp?.name,
                email: resp?.email,
            });
            const isAdmin = resp?.is_admin || false;
            const userToStore = { ...resp, is_admin: isAdmin };

            localStorage.setItem(LOCAL_STORAGE_LOGGED_IN_USER_DATA, JSON.stringify(userToStore));
            localStorage.setItem(LOCAL_STORAGE_JWT_TOKEN, action.payload.token);
            localStorage.setItem(USER_LOGIN_METHOD, action.payload.loginMethod);
            Communications.updateLoginUserTokenSubject.next(action.payload.token);

            return {
                ...state,
                isNewUser: !resp?.onBoarding,
                user: userToStore,
                token: action.payload.token,
                loginMethod: action.payload.loginMethod,
                isAdmin: isAdmin, // Added
                originalAdminState: null, // Clear impersonation state
            };
        }

        case USER_LOGOUT: {
            localStorage.removeItem('originalAdminState'); // Clear any impersonation state
            const loginMethod = localStorage.getItem(USER_LOGIN_METHOD);
            if (loginMethod === "google") {
                googleLogout();
            }

            localStorage.removeItem(LOCAL_STORAGE_LOGGED_IN_USER_DATA);
            localStorage.removeItem(LOCAL_STORAGE_JWT_TOKEN);
            localStorage.removeItem(USER_LOGIN_METHOD);
            Communications.updateLoginUserTokenSubject.next(undefined);

            return {
                ...state,
                isNewUser: false,
                user: null,
                token: null,
                loginMethod: null,
                isAdmin: false, // Added
                originalAdminState: null, // Clear impersonation state
            };
        }

        case UPDATE_ROLE: {
            // This case might also need to update isAdmin if role changes to/from admin
            const newRole = action.payload.role;
            const isAdmin = !!(state.user?.is_admin || newRole === 'admin');
            const updatedUser = {
                ...state.user,
                onBoarding: true,
                role: newRole,
                is_admin: isAdmin, // Ensure is_admin is consistent with role
            };

            localStorage.setItem(LOCAL_STORAGE_LOGGED_IN_USER_DATA, JSON.stringify(updatedUser));

            return {
                ...state,
                isNewUser: false,
                user: updatedUser,
                isAdmin: isAdmin, // Updated
            };
        }

        case SET_NEW_USER:
            return {
                ...state,
                isNewUser: action.payload,
            };

        case SET_CURRENT_PROMPT:
            return {
                ...state,
                currentPrompt: action.payload,
            };

        case START_IMPERSONATION: {
            if (!state.isAdmin) { // Safety check: only admins can start impersonation
                console.warn("User is not an admin. Cannot start impersonation.");
                return state;
            }
            const targetUser = action.payload.user as IUser;
            const targetToken = action.payload.token;

            // Determine isAdmin status for the target user
            const targetUserIsAdmin = !!(targetUser?.is_admin || targetUser?.role === 'admin');
            const userToStore = { ...targetUser, is_admin: targetUserIsAdmin };


            // Save current admin state
            const adminStateToSave = {
                user: state.user,
                token: state.token,
                isAdmin: state.isAdmin, // Should be true here
            };

            localStorage.setItem('originalAdminState', JSON.stringify(adminStateToSave)); // Persist for resilience
            localStorage.setItem(LOCAL_STORAGE_LOGGED_IN_USER_DATA, JSON.stringify(userToStore));
            localStorage.setItem(LOCAL_STORAGE_JWT_TOKEN, targetToken);
            // loginMethod might need to be cleared or set to a generic value for impersonation
            // localStorage.setItem(USER_LOGIN_METHOD, 'impersonation');

            Communications.updateLoginUserTokenSubject.next(targetToken);
            commonService.handleMixpanelIdentify(userToStore); // Update analytics identity
            commonService.handleMixpanelTrack("Impersonation Started", { targetUserEmail: userToStore.email });


            return {
                ...state,
                originalAdminState: adminStateToSave,
                user: userToStore,
                token: targetToken,
                isAdmin: targetUserIsAdmin, // Reflecting the impersonated user's admin status
                // isNewUser might need to be re-evaluated or set to false
                isNewUser: !targetUser?.onBoarding,
            };
        }

        case STOP_IMPERSONATION: {
            const storedAdminStateString = localStorage.getItem('originalAdminState');
            if (!state.originalAdminState && !storedAdminStateString) {
                console.warn("No original admin state found to stop impersonation.");
                // Attempt to logout or reset to a safe state if originalAdminState is missing
                // This part might need more robust handling depending on app requirements
                return { ...initialState, user: null, token: null, isAdmin: false, originalAdminState: null };
            }

            const originalAdminState = state.originalAdminState || (storedAdminStateString ? JSON.parse(storedAdminStateString) : null);

            if (!originalAdminState) { // Should not happen if previous check is robust
                console.error("Failed to retrieve original admin state.");
                // Fallback to logout
                localStorage.removeItem(LOCAL_STORAGE_LOGGED_IN_USER_DATA);
                localStorage.removeItem(LOCAL_STORAGE_JWT_TOKEN);
                localStorage.removeItem(USER_LOGIN_METHOD);
                localStorage.removeItem('originalAdminState');
                Communications.updateLoginUserTokenSubject.next(undefined);
                return { ...initialState, user: null, token: null, isAdmin: false, originalAdminState: null };
            }

            const adminUserToRestore = originalAdminState.user;
            const adminTokenToRestore = originalAdminState.token;

            localStorage.setItem(LOCAL_STORAGE_LOGGED_IN_USER_DATA, JSON.stringify(adminUserToRestore));
            localStorage.setItem(LOCAL_STORAGE_JWT_TOKEN, adminTokenToRestore as string);
            // localStorage.setItem(USER_LOGIN_METHOD, originalAdminState.loginMethod || 'username'); // Restore original login method
            localStorage.removeItem('originalAdminState');

            Communications.updateLoginUserTokenSubject.next(adminTokenToRestore);
            if (adminUserToRestore) {
                commonService.handleMixpanelIdentify(adminUserToRestore); // Restore analytics identity
                commonService.handleMixpanelTrack("Impersonation Stopped", {});
            }

            return {
                ...state,
                user: adminUserToRestore,
                token: adminTokenToRestore,
                isAdmin: originalAdminState.isAdmin, // Should be true
                originalAdminState: null,
                isNewUser: !adminUserToRestore?.onBoarding, // Or based on admin's actual onBoarding status
            };
        }

        default:
            return state;
    }
};

export default authReducer;
